/* React Components */
import { supabase } from '../services/Supabase';
import { useLocation } from "react-router-dom";

const CommonFunction = () => {
    const location = useLocation();

    const hasValues = (obj, keys) => {
        return keys.every(key => obj[key]);
    };

    const parseError = (error) => {
        if (error && typeof error === "object") {
            const code = error.code || "UNKNOWN_ERROR";
            const message = error.message || "";
            const details = error.details || "";

            // Supabase/Postgres database errors
            if (code === "23505") {
                if (details.includes("(user_email)") || message.includes("user_email")) {
                    return {
                        code,
                        message: "This email is already registered. Please use a different email address.",
                    };
                }

                return {
                    code,
                    message: "This record already exists. Please use a different value.",
                };
            }

            if (code === "22P02") {
                return {
                    code,
                    message: "One of the values entered is invalid. Please check your input and try again.",
                };
            }

            if (code === "23502") {
                return {
                    code,
                    message: "Please complete all required fields.",
                };
            }

            if (code === "PGRST116") {
                return {
                    code,
                    message: "No matching record was found.",
                };
            }

            // Auth / rate limit style messages
            if (
                message.toLowerCase().includes("rate limit") ||
                message.toLowerCase().includes("too many requests") ||
                message.toLowerCase().includes("email rate limit exceeded") ||
                message.toLowerCase().includes("over_email_send_rate_limit")
            ) {
                return {
                    code,
                    message: "Too many email requests were sent. Please wait a bit before trying again.",
                };
            }

            if (
                message.toLowerCase().includes("email not confirmed")
            ) {
                return {
                    code,
                    message: "Your email address is not yet confirmed. Please check your inbox and verify your email.",
                };
            }

            return {
                code,
                message: message || details || "Something went wrong. Please try again.",
            };
        }

        if (typeof error === "string") {
            const lower = error.toLowerCase();

            if (
                lower.includes("rate limit") ||
                lower.includes("too many requests") ||
                lower.includes("email rate limit exceeded") ||
                lower.includes("over_email_send_rate_limit")
            ) {
                return {
                    code: "RATE_LIMIT",
                    message: "Too many email requests were sent. Please wait a bit before trying again.",
                };
            }

            const errorParts = error.split(":");
            return {
                code: errorParts[0]?.trim() || "UNKNOWN_ERROR",
                message: errorParts.slice(1).join(":").trim() || "Something went wrong.",
            };
        }

        return {
            code: "UNKNOWN_ERROR",
            message: "Something went wrong. Please try again.",
        };
    };

    const convertN2lbr = (msg)=>{
        const messageWithLineBreaks = msg.replace(/\n/g, '<br>');
        return messageWithLineBreaks;
    }
    
    const convertTextPattern = (msg) => {
        if (!msg) return "";

        let cleaned = msg;

        // ✅ Remove markdown headings like ###  (even after <br>)
        cleaned = cleaned.replace(/(^|<br>\s*)#{1,6}\s*/g, "$1");

        // ✅ Convert **bold** to styled span
        cleaned = cleaned.replace(/\*\*(.*?)\*\*/g, (_, text) => {
            return `<span class="response-heading">${text}</span>`;
        });

        return cleaned;
    };

    const getNicknameFromEmail = (email) => {
        if (typeof email !== 'string') return '';
        const atIndex = email.indexOf('@');
        if (atIndex === -1) return '';
        return email.substring(0, atIndex);
    };

    const commonQuestionsList = async () => {
        // eslint-disable-next-line
        let { data: common_questions, error } = await supabase.from('common_questions').select('*, category_id(category_name)');
        return common_questions;
    };

    const isRouteActive = (path) => {
        // Convert wildcard pattern to regex
        const pattern = path.replace(/\*/g, '.*');
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(location.pathname);
    };

    const getDataArray = (messages) => {
        // return empty array if null or undefined
        if (!messages) return [];

        // if already array, return directly
        if (Array.isArray(messages)) return messages;

        // if JSON string, parse safely
        if (typeof messages === "string") {
            try {
                const parsed = JSON.parse(messages); // parse JSON
                return Array.isArray(parsed) ? parsed : []; // ensure array
            } catch (err) {
                console.error("Invalid JSON format"); // invalid JSON
                return [];
            }
        }

        // fallback return empty array
        return [];
    };

    const formatRelativeDate = (isoString) => {
        if (!isoString) return "";

        const date = new Date(isoString);
        const now = new Date();

        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        const timeString = date.toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
        });

        if (diffDays === 0) {
            return `Today, ${timeString}`;
        }

        if (diffDays === 1) {
            return `Yesterday, ${timeString}`;
        }

        if (diffDays < 7) {
            return `${diffDays} days ago`;
        }

        const diffWeeks = Math.floor(diffDays / 7);
        if (diffWeeks < 4) {
            return `${diffWeeks} ${diffWeeks === 1 ? "week" : "weeks"} ago`;
        }

        const diffMonths = Math.floor(diffDays / 30);
        if (diffMonths < 12) {
            return `${diffMonths} ${diffMonths === 1 ? "month" : "months"} ago`;
        }

        const diffYears = Math.floor(diffDays / 365);
        return `${diffYears} ${diffYears === 1 ? "year" : "years"} ago`;
    };

    const mainSystemInstructions = async () => {
        const { data, error } = await supabase
            .from('system_instructions')
            .select('instruction_text')
            .order('id', { ascending: true });

        if (error) {
            console.error('Error fetching system instructions:', error);
            return '';
        }

        if (!data || data.length === 0) return '';

        // Combine all instructions into one string
        const combinedInstructions = data
            .map(row => row.instruction_text)
            .join('\n\n');

        return combinedInstructions;
    };

    const getCategoryDetails = async (id) => {
        const { data, error } = await supabase
            .from("coaching_categories")
            .select("*")
            .eq("id", id)
        .maybeSingle();

        if (error) {
            console.error('Error fetching system instructions:', error);
            return [];
        }

        if (!data || data.length === 0) return [];
        return data;
    };

    return { hasValues, getDataArray, parseError, convertN2lbr, convertTextPattern, getNicknameFromEmail, commonQuestionsList, isRouteActive, formatRelativeDate, mainSystemInstructions, getCategoryDetails };
};

export default CommonFunction;